import { useEffect, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import isEmpty from 'lodash/isEmpty';

import { getTheme } from '@talend/react-components/lib/theme';
import Forms from '@talend/react-forms';

import { I18N_NAME_SPACE } from '../../../../../constants';
import { CADFormModel } from '../../../CAD.types';
import { Mandatory } from '../Mandatory';

import scss from './Enum.module.css';

const theme = getTheme(scss);
const Enumeration = Forms.UIForm.utils.widgets.enumeration;
const Datalist = Forms.UIForm.utils.widgets.datalist;

const formatItemsForDataList = (items: string[]) => items.map(name => ({ name, value: name }));
const hasEmptyValues = (items: string[]) => items.some(element => !element);
export const validateEnumeration = (value?: string[]) =>
	!!(value && value.length && !hasEmptyValues(value));

export interface EnumProps {
	id: string;
	rhf: UseFormReturn<CADFormModel>;
	readOnly: boolean;
}

export function Enum({ id, rhf, readOnly }: EnumProps) {
	const { t } = useTranslation(I18N_NAME_SPACE);
	const [mandatory, values = [], defaults] = rhf.watch(['mandatory', 'values', 'defaults']);
	const datalistItems = useMemo(() => formatItemsForDataList(values), [values]);

	useEffect(() => {
		if (rhf.formState.isSubmitted) {
			rhf.trigger('defaults');
		}
	}, [mandatory]);

	useEffect(() => {
		let newDefault = defaults;

		// reset defaults if enum was removed
		if (!defaults || (defaults && !values.find(value => value === defaults))) {
			newDefault = '';
		}
		// set defaults to first enum if mandatory
		if (mandatory && !defaults && values.length) {
			newDefault = values[0];
		}
		if (newDefault !== defaults) {
			rhf.setValue('defaults', newDefault);
			rhf.trigger('values');
			rhf.trigger('defaults');
		}
	}, [mandatory, defaults, values, rhf]);

	return (
		<div data-testid="cad-enumeration" test-id="cad-enumeration" className={theme('tdc-cad-enum')}>
			<Controller
				render={({ field, formState }) => (
					<Enumeration
						onChange={(
							_: any,
							val: {
								schema: { disabled: boolean; required: boolean };
								value: [{ values: string[] }];
							},
						) => field.onChange(val.value.flatMap(({ values }) => values))}
						value={(field.value || []).map((value: string) => ({ values: [value] }))}
						id={`${id}-values`}
						schema={{
							required: true,
							disabled: readOnly,
						}}
						onFinish={field.onBlur}
						errorMessage={t('ERROR_OBJECT_REQUIRED', 'Missing required field')}
						isValid={!formState.errors.values}
					/>
				)}
				control={rhf.control}
				name="values"
				rules={{
					validate: validateEnumeration,
				}}
			/>
			<Mandatory rhf={rhf} readOnly={readOnly} />
			<Controller
				render={({ field, formState }) => (
					<Datalist
						onChange={(_: any, val: any) => field.onChange(val.value)}
						value={field.value}
						id={`${id}-defaults`}
						onFinish={field.onBlur}
						schema={{
							schema: {
								type: 'string',
							},
							title: t('CAD-LIST_OF_VALUES_DEFAULT_VALUE', 'Default value'),
							placeholder: t(
								'CUSTOM_ATTRIBUTES_PLACEHOLDER_ENUM_DEFAULT_VALUE',
								'Select a default value',
							),
							titleMap: datalistItems,
							required: mandatory,
							readOnly,
						}}
						isValid={!formState.errors.defaults}
						errorMessage={t('ERROR_DEFAULT_INVALID', 'Please select a value')}
					/>
				)}
				control={rhf.control}
				name="defaults"
				rules={{
					validate: {
						required: value => !rhf.getValues().mandatory || !isEmpty(value),
					},
				}}
			/>
		</div>
	);
}

Enum.displayName = 'Enum';
